import * as React from 'react';
import styles from './index.less';
import FComponentsLib from '@freelog/components-lib';
import { FI18n, FServiceAPI } from '@freelog/tools-lib';
import FTooltip from '@/components/FTooltip';
import * as AHooks from 'ahooks';
import { InputRef, Space } from 'antd';
import FIcons from '@/components/FIcons';
import FInput_PinyinSafeTextCounter from '../FInput_PinyinSafeTextCounter';

interface FResourceLabelEditorProps {
  value: string[];
  resourceTypeCode: string;
  onChange?: (value: string[]) => void;
}

interface FResourceLabelEditorStates {
  labels1: {
    id: string;
    name: string;
    description: string;
  }[];
  labels2: {
    id: string;
    name: string;
    description: string;
  }[];

  input: string;
  inputError: string;
}

const initState: FResourceLabelEditorStates = {
  labels1: [],
  labels2: [],

  input: '',
  inputError: '',
};

function FResourceLabelEditor($prop: FResourceLabelEditorProps) {
  const inputElementRef = React.useRef<InputRef>(null);

  const [$state, $setState] = AHooks.useSetState<FResourceLabelEditorStates>(initState);

  AHooks.useMount(async () => {
    if ($prop.resourceTypeCode === '') {
      return;
    }

    const {
      data: data_availableTags,
    }: {
      data: {
        tagId: string;
        tagType: 1 | 2;
        tagName: string;
        tagNote: string;
      }[];
    } = await FServiceAPI.Resource.availableTags({
      resourceTypeCode: $prop.resourceTypeCode,
    });
    $setState({
      labels1: data_availableTags
        .filter((d) => {
          return d.tagType === 1;
        })
        .map((d) => {
          return {
            id: d.tagId,
            name: d.tagName,
            description: d.tagNote || '',
          };
        }),
      labels2: data_availableTags
        .filter((d) => {
          return d.tagType === 2;
        })
        .map((d) => {
          return {
            id: d.tagId,
            name: d.tagName,
            description: d.tagNote || '',
          };
        }),
    });
  });

  React.useEffect(() => {
    if ($state.input === '') {
      return;
    }
    $setState({
      inputError: $prop.value.includes($state.input)
        ? // '不能有重复'
          FI18n.i18nNext.tAuto('form_input_tag_error_existed')
        : $state.inputError ===
          // '不能有重复'
          FI18n.i18nNext.tAuto('form_input_tag_error_existed')
        ? ''
        : $state.inputError,
    });
  }, [$prop.value]);

  if ($prop.value.length >= 20) {
    return (
      <div>
        <div className={styles.selectedLabels}>
          {$prop.value.map((v, w) => {
            return (
              <label key={v} className={styles.selectedLabel}>
                <span>{v}</span>
                <FIcons.FClose
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    $prop.onChange && $prop.onChange($prop.value.filter((i, j) => j !== w));
                  }}
                />
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Space size={20}>
        <FInput_PinyinSafeTextCounter
          ref={inputElementRef}
          lengthLimit={-1}
          value={$state.input}
          placeholder={FI18n.i18nNext.t('hint_add_resource_tag')}
          className={[styles.Input].join(' ')}
          onChangeValue={(v) => {
            const value = v.replaceAll('#', '');
            $setState({
              input: value,
            });

            let errorText: string = '';

            if (value.length > 20) {
              errorText =
                // 不超过20个字符
                FI18n.i18nNext.tAuto('form_input_tag_error_length');
            } else if ($prop.value.includes(value)) {
              errorText =
                // 不能有重复
                FI18n.i18nNext.tAuto('form_input_tag_error_existed');
            }
            $setState({
              inputError: errorText,
            });
          }}
          onPressEnter={() => {
            if ($state.inputError !== '') {
              return;
            }
            if ($state.input === '') {
              $setState({
                inputError: '不能为空',
              });
              return;
            }
            $setState({
              input: '',
            });
            $prop.onChange &&
              $prop.onChange([...$prop.value, $state.input.replace(new RegExp(/#/, 'g'), '')]);
          }}
          onKeyUp={(event) => {
            if (event.key === 'Escape') {
              $setState({
                input: '',
                inputError: '',
              });
              inputElementRef.current?.blur();
            }
          }}
        />
        <FComponentsLib.FContentText
          text={
            // 还可添加${20 - $prop.value.length}个标签
            FI18n.i18nNext.tAuto('addtags_hint', {
              TagQty: 20 - $prop.value.length,
            })
          }
          type={'additional2'}
        />
      </Space>
      {$state.inputError !== '' && (
        <>
          <div style={{ height: 5 }} />
          <div className={styles.errorTip}>{$state.inputError}</div>
        </>
      )}

      {$prop.value.length > 0 && (
        <>
          <div style={{ height: 20 }} />
          <div className={styles.selectedLabels}>
            {$prop.value.map((v, w) => {
              return (
                <label key={v} className={styles.selectedLabel}>
                  <span>{v}</span>
                  <FIcons.FClose
                    style={{
                      fontSize: 12,
                    }}
                    onClick={() => {
                      $prop.onChange?.($prop.value.filter((i, j) => j !== w));
                    }}
                  />
                </label>
              );
            })}
          </div>
        </>
      )}

      {$state.labels1.length > 0 && (
        <>
          <div style={{ height: 25 }} />

          <FComponentsLib.FContentText
            text={
              // 推荐标签
              FI18n.i18nNext.tAuto('addtags_recommended01')
            }
            type={'additional2'}
          />
          <div style={{ height: 10 }} />
          <div className={styles.Labels}>
            {$state.labels1.map((l) => {
              const selected: boolean = $prop.value.includes(l.name);
              return (
                <FTooltip
                  title={l.description.substring(0, 100)}
                  placement={'top'}
                  key={l.id}
                  open={l.description === '' ? false : undefined}
                >
                  <label
                    onClick={() => {
                      if (selected) {
                        $prop.onChange &&
                          $prop.onChange(
                            $prop.value.filter((v) => {
                              return v !== l.name;
                            }),
                          );
                      } else {
                        $prop.onChange && $prop.onChange([...$prop.value, l.name]);
                      }
                    }}
                    className={[
                      styles.Label,
                      $prop.value.includes(l.name) ? styles.selected : '',
                    ].join(' ')}
                  >
                    {l.name}
                  </label>
                </FTooltip>
              );
            })}
          </div>
        </>
      )}

      {$state.labels2.length > 0 && (
        <>
          <div style={{ height: 25 }} />

          <FComponentsLib.FContentText
            text={
              // 推荐活动:
              FI18n.i18nNext.tAuto('addtags_recommended02')
            }
            type={'additional2'}
          />
          <div style={{ height: 10 }} />
          <div className={styles.Labels}>
            {$state.labels2.map((l) => {
              const selected: boolean = $prop.value.includes(l.name);
              return (
                <FTooltip
                  title={l.description.substring(0, 100)}
                  placement={'top'}
                  key={l.id}
                  open={l.description === '' ? false : undefined}
                >
                  <label
                    onClick={() => {
                      if (selected) {
                        $prop.onChange &&
                          $prop.onChange(
                            $prop.value.filter((v) => {
                              return v !== l.name;
                            }),
                          );
                      } else {
                        $prop.onChange && $prop.onChange([...$prop.value, l.name]);
                      }
                    }}
                    className={[
                      styles.Label,
                      $prop.value.includes(l.name) ? styles.selected : '',
                    ].join(' ')}
                  >
                    {l.name}
                  </label>
                </FTooltip>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default FResourceLabelEditor;
